'use client';

import { cloneElement, createContext, isValidElement, ReactNode, useCallback, useContext } from 'react';

type Dictionary = Record<string, any>;

interface I18nContextValue {
  dictionary: Dictionary;
  locale: string;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  dictionary: Dictionary;
  locale: string;
  children: ReactNode;
}

export const I18nProvider = ({ dictionary, locale, children }: I18nProviderProps) => {
  return (
    <I18nContext.Provider value={{ dictionary, locale }}>
      {children}
    </I18nContext.Provider>
  );
};

// Translation options interface
interface TranslationOptions {
  count?: number;
  defaultValue?: string;
  [key: string]: any;
}

const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }

  const { dictionary, locale } = context;

  const getNestedValue = (obj: any, path: string): string | undefined => {
    if (!path || !obj || typeof path !== 'string') return undefined;
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  const t = useCallback((key: string, options?: TranslationOptions): string | ReactNode => {
    let translationKey = key;

    // Handle pluralization based on count
    if (options?.count !== undefined) {
      const count = options.count;
      let suffix: string;

      if (count === 0) {
        suffix = '_zero';
      } else if (count === 1) {
        suffix = '_one';
      } else {
        suffix = '_other';
      }

      // Try to find the pluralized key first
      const pluralizedKey = `${key}${suffix}`;
      const pluralizedValue = getNestedValue(dictionary, pluralizedKey);

      if (pluralizedValue !== undefined) {
        translationKey = pluralizedKey;
      }
    }

    // Get the translation value
    const value = getNestedValue(dictionary, translationKey);
    
    if (value === undefined) {
      if (options?.defaultValue) {
        return options.defaultValue;
      }
      return key;
    }

    const translationString = String(value);

    // If no options for interpolation, return the string as-is
    if (!options || Object.keys(options).filter(k => k !== 'defaultValue').length === 0) {
      return translationString;
    }

    // Handle interpolation
    return interpolateTranslation(translationString, options);
  }, [dictionary]);

  return { t, ts: (key: string, options?: TranslationOptions) => t(key, options) as string, dictionary, locale };
};

function interpolateTranslation(template: string, options: TranslationOptions): string | ReactNode {
  // Regular expression to find interpolation patterns: {{key}} or {{key:component}}
  const interpolationRegex = /\{\{([^}]+)\}\}/g;
  const parts: (string | ReactNode)[] = [];
  let lastIndex = 0;
  let match;
  let hasReactComponents = false;

  while ((match = interpolationRegex.exec(template)) !== null) {
    // Add text before the interpolation
    if (match.index > lastIndex) {
      parts.push(template.slice(lastIndex, match.index));
    }

    const interpolationKey = match[1].trim();
    let value = options[interpolationKey];

    // Handle React components
    if (isValidElement(value)) {
      hasReactComponents = true;
      parts.push(cloneElement(value as React.ReactElement, { key: `interpolation-${match.index}` }));
    } else if (value !== undefined) {
      parts.push(String(value));
    } else {
      // Keep the original interpolation if no value found
      parts.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < template.length) {
    parts.push(template.slice(lastIndex));
  }

  // If no React components, return as string
  if (!hasReactComponents) {
    return parts.join('');
  }

  // Return React fragment for components
  return parts.length === 1 ? parts[0] : parts;
}

export const useTranslation = (localeOverride?: string, ...namespaces: string[]) => {
  const { t, ts, dictionary, locale } = useI18n();
  return { t, ts, dictionary, locale: localeOverride || locale };
};